import api from './api';
import {
  Deal,
  CreateDealData,
  UpdateDealData,
  DealFilters,
  DealStats
} from '../types/deal';

export interface DealListResponse {
  deals: Deal[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const dealService = {
  // Get all deals
  getAll: async (
    workspaceId: string,
    filters?: DealFilters,
    page?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ): Promise<DealListResponse> => {
    const params: any = {
      page: page || 1,
      limit: limit || 20,
      sortBy: sortBy || 'createdAt',
      sortOrder: sortOrder || 'desc',
      ...filters
    };

    // Convert array filters to comma-separated strings
    if (filters?.stage && Array.isArray(filters.stage)) {
      params.stage = filters.stage.join(',');
    }
    if (filters?.dealType && Array.isArray(filters.dealType)) {
      params.dealType = filters.dealType.join(',');
    }

    const response = await api.get(`/workspaces/${workspaceId}/deals`, { params });
    return response.data;
  },

  // Get deal by ID
  getById: async (
    workspaceId: string,
    dealId: string,
    include?: string[]
  ): Promise<{ deal: Deal }> => {
    const params: any = {};
    if (include && include.length > 0) {
      params.include = include.join(',');
    }

    const response = await api.get(`/workspaces/${workspaceId}/deals/${dealId}`, { params });
    return response.data;
  },

  // Create deal
  create: async (workspaceId: string, data: CreateDealData): Promise<{ deal: Deal }> => {
    const response = await api.post(`/workspaces/${workspaceId}/deals`, data);
    return response.data;
  },

  // Update deal
  update: async (
    workspaceId: string,
    dealId: string,
    data: UpdateDealData
  ): Promise<{ deal: Deal }> => {
    const response = await api.put(`/workspaces/${workspaceId}/deals/${dealId}`, data);
    return response.data;
  },

  // Delete deal
  delete: async (workspaceId: string, dealId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/workspaces/${workspaceId}/deals/${dealId}`);
    return response.data;
  },

  // Update deal stage
  updateStage: async (
    workspaceId: string,
    dealId: string,
    stage: string,
    notes?: string
  ): Promise<{ deal: Deal }> => {
    const response = await api.post(`/workspaces/${workspaceId}/deals/${dealId}/update-stage`, {
      stage,
      notes
    });
    return response.data;
  },

  // Get deal statistics
  getStats: async (workspaceId: string): Promise<DealStats> => {
    const response = await api.get(`/workspaces/${workspaceId}/deals/stats`);
    return response.data;
  },

  // Assign unit to deal
  assignUnit: async (
    workspaceId: string,
    dealId: string,
    unitId: string
  ): Promise<{ deal: Deal }> => {
    const response = await api.post(`/workspaces/${workspaceId}/deals/${dealId}/assign-unit`, {
      unitId
    });
    return response.data;
  },

  // Release unit from deal
  releaseUnit: async (
    workspaceId: string,
    dealId: string,
    reason?: string
  ): Promise<{ deal: Deal }> => {
    const response = await api.post(`/workspaces/${workspaceId}/deals/${dealId}/release-unit`, {
      reason
    });
    return response.data;
  }
};
